import { useState, useRef, useCallback } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { 
  Plus, 
  Trash2, 
  Copy, 
  Eye, 
  EyeOff, 
  GripVertical,
  Settings,
  ChevronUp,
  ChevronDown,
  Move,
  Layout,
  MousePointer
} from "lucide-react";
import { PageElement } from "../VisualPageBuilder";
import ElementRenderer from "./ElementRenderer";

interface DesignCanvasProps {
  elements: PageElement[];
  selectedElement: PageElement | null;
  onSelectElement: (element: PageElement | null) => void;
  onUpdateElement: (elementId: string, updates: Partial<PageElement>) => void;
  onDeleteElement: (elementId: string) => void;
  activeView: 'desktop' | 'tablet' | 'mobile';
  globalStyles: any;
}

export default function DesignCanvas({
  elements,
  selectedElement,
  onSelectElement,
  onUpdateElement,
  onDeleteElement,
  activeView,
  globalStyles
}: DesignCanvasProps) {
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);
  const [layoutMode, setLayoutMode] = useState<'linear' | 'free'>('linear');
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Ensure elements is always an array
  const safeElements = elements || [];

  // Get canvas width based on active view
  const getCanvasWidth = () => {
    switch (activeView) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      case 'desktop':
      default:
        return '100%';
    }
  };

  const getCanvasMaxWidth = () => {
    switch (activeView) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      case 'desktop':
      default:
        return '1200px';
    }
  };

  // Handle element selection
  const handleElementClick = useCallback((element: PageElement, event: React.MouseEvent) => {
    event.stopPropagation();
    onSelectElement(element);
  }, [onSelectElement]);

  // Handle canvas click (deselect)
  const handleCanvasClick = useCallback(() => {
    onSelectElement(null);
  }, [onSelectElement]);

  // Duplicate element
  const duplicateElement = useCallback((element: PageElement) => {
    const duplicated: PageElement = {
      ...element,
      id: `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `${element.name} (Kopie)`,
      order: element.order + 1,
      styles: {
        ...element.styles,
        left: layoutMode === 'free' ? `${(parseFloat(element.styles.left || '0') + 20)}px` : element.styles.left,
        top: layoutMode === 'free' ? `${(parseFloat(element.styles.top || '0') + 20)}px` : element.styles.top
      }
    };
    
    // We need to add this through the parent component
    // For now, we'll just select the original element
    onSelectElement(element);
  }, [onSelectElement, layoutMode]);

  // Toggle element visibility
  const toggleVisibility = useCallback((elementId: string, currentVisibility: boolean) => {
    onUpdateElement(elementId, {
      settings: { isVisible: !currentVisibility }
    });
  }, [onUpdateElement]);

  // Move element up/down (linear mode only)
  const moveElement = useCallback((elementId: string, direction: 'up' | 'down') => {
    if (layoutMode !== 'linear') return;
    
    const elementIndex = safeElements.findIndex(el => el.id === elementId);
    if (elementIndex === -1) return;

    const newIndex = direction === 'up' ? elementIndex - 1 : elementIndex + 1;
    if (newIndex < 0 || newIndex >= safeElements.length) return;

    // This would need to be handled by the parent component
    console.log(`Moving element ${elementId} ${direction}`);
  }, [safeElements, layoutMode]);

  // Handle free positioning drag
  const handleMouseDown = useCallback((elementId: string, event: React.MouseEvent) => {
    if (layoutMode !== 'free') return;
    
    event.preventDefault();
    event.stopPropagation();
    setDraggedElement(elementId);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const canvasRect = canvas.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    
    const element = safeElements.find(el => el.id === elementId);
    if (!element) return;
    
    const startLeft = parseFloat(element.styles.left || '50');
    const startTop = parseFloat(element.styles.top || '50');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      const canvasWidth = canvasRect.width;
      const canvasHeight = Math.max(canvasRect.height, 600);
      
      const newLeft = Math.max(0, Math.min(canvasWidth - 100, startLeft + deltaX));
      const newTop = Math.max(0, Math.min(canvasHeight - 50, startTop + deltaY));
      
      onUpdateElement(elementId, {
        styles: {
          ...element.styles,
          position: 'absolute',
          left: `${newLeft}px`,
          top: `${newTop}px`
        }
      });
    };

    const handleMouseUp = () => {
      setDraggedElement(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [layoutMode, safeElements, onUpdateElement]);

  // Initialize elements for free mode (set initial positions if not set)
  const initializeFreeMode = useCallback(() => {
    safeElements.forEach((element, index) => {
      if (!element.styles.left || !element.styles.top) {
        onUpdateElement(element.id, {
          styles: {
            ...element.styles,
            position: 'absolute',
            left: `${50 + (index % 3) * 220}px`,
            top: `${50 + Math.floor(index / 3) * 150}px`
          }
        });
      }
    });
  }, [safeElements, onUpdateElement]);

  // Switch to free mode and initialize positions
  const switchToFreeMode = useCallback(() => {
    setLayoutMode('free');
    setTimeout(() => initializeFreeMode(), 100);
  }, [initializeFreeMode]);

  // Adjust element positions when switching device views
  const adjustPositionsForDevice = useCallback(() => {
    if (layoutMode !== 'free') return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const canvasWidth = canvas.offsetWidth;
    const canvasHeight = Math.max(canvas.offsetHeight, 600);
    
    // Get safe margins based on device
    const marginX = activeView === 'mobile' ? 20 : activeView === 'tablet' ? 40 : 60;
    const marginY = 20;
    
    const maxAllowedX = canvasWidth - marginX;
    const maxAllowedY = canvasHeight - marginY;
    
    safeElements.forEach(element => {
      if (element.styles.position === 'absolute') {
        const currentX = parseFloat(element.styles.left || '0');
        const currentY = parseFloat(element.styles.top || '0');
        
        let adjustedX = currentX;
        let adjustedY = currentY;
        let needsUpdate = false;
        
        // Adjust X position if element is out of bounds
        if (currentX > maxAllowedX) {
          adjustedX = Math.max(marginX / 2, maxAllowedX - 100);
          needsUpdate = true;
        }
        
        // Adjust Y position if element is out of bounds 
        if (currentY > maxAllowedY) {
          adjustedY = Math.max(marginY, maxAllowedY - 50);
          needsUpdate = true;
        }
        
        // For very small viewports, scale positions proportionally
        if (activeView === 'mobile' && currentX > 300) {
          adjustedX = Math.min(currentX * 0.7, maxAllowedX - 50);
          needsUpdate = true;
        }
        
        if (needsUpdate) {
          onUpdateElement(element.id, {
            styles: {
              ...element.styles,
              left: `${adjustedX}px`,
              top: `${adjustedY}px`
            }
          });
        }
      }
    });
  }, [layoutMode, safeElements, onUpdateElement, activeView]);

  // Call adjustment when device view changes
  React.useEffect(() => {
    if (layoutMode === 'free') {
      setTimeout(() => adjustPositionsForDevice(), 200);
    }
  }, [activeView, adjustPositionsForDevice]);

  return (
    <div className="flex-1 bg-muted/20 p-4 overflow-hidden">
      <div className="h-full flex flex-col items-center">
        {/* Canvas Header */}
        <div className="w-full max-w-5xl mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="text-sm text-muted-foreground">
                Arbeitsfläche ({activeView})
              </div>
              <div className="text-xs text-muted-foreground">
                {safeElements.length} Element{safeElements.length !== 1 ? 'e' : ''}
              </div>
            </div>
            
            {/* Layout Mode Toggle */}
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant={layoutMode === 'linear' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setLayoutMode('linear')}
                className="gap-2"
              >
                <Layout className="w-4 h-4" />
                Linear
              </Button>
              <Button
                variant={layoutMode === 'free' ? 'default' : 'ghost'}
                size="sm"
                onClick={switchToFreeMode}
                className="gap-2"
              >
                <MousePointer className="w-4 h-4" />
                Frei
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSelectElement(null)}
            >
              Auswahl aufheben
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div 
          ref={canvasRef}
          className="bg-background border rounded-lg shadow-lg overflow-hidden transition-all duration-300 relative"
          style={{ 
            width: getCanvasWidth(),
            maxWidth: getCanvasMaxWidth(),
            minHeight: '800px'
          }}
          onClick={handleCanvasClick}
        >
          {safeElements.length === 0 ? (
            // Empty state
            <div className="h-full min-h-[600px] flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-24 h-24 mx-auto bg-muted rounded-full flex items-center justify-center">
                  <Plus className="w-8 h-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Beginnen Sie hier</h3>
                  <p className="text-muted-foreground max-w-md">
                    Ziehen Sie Elemente aus der Palette hierher, um Ihre Landing Page zu erstellen.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Modus: {layoutMode === 'linear' ? 'Linear (untereinander)' : 'Frei positionierbar'}
                  </p>
                </div>
              </div>
            </div>
          ) : layoutMode === 'linear' ? (
            // Linear Layout Mode (Original DnD)
            <Droppable droppableId="canvas">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-h-full relative ${
                    snapshot.isDraggingOver ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="relative">
                    {elements
                      .sort((a, b) => a.order - b.order)
                      .map((element, index) => (
                        <Draggable
                          key={element.id}
                          draggableId={element.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`relative group ${
                                snapshot.isDragging ? 'z-50' : ''
                              } ${
                                !element.settings.isVisible ? 'opacity-50' : ''
                              }`}
                              onMouseEnter={() => setHoveredElement(element.id)}
                              onMouseLeave={() => setHoveredElement(null)}
                            >
                              {/* Element Wrapper with Selection Outline */}
                              <div
                                className={`relative ${
                                  selectedElement?.id === element.id
                                    ? 'ring-2 ring-primary ring-offset-2'
                                    : hoveredElement === element.id
                                    ? 'ring-1 ring-primary/50'
                                    : ''
                                }`}
                                onClick={(e) => handleElementClick(element, e)}
                              >
                                {/* Linear Mode Toolbar */}
                                {(selectedElement?.id === element.id || hoveredElement === element.id) && (
                                  <div className="absolute -top-10 left-0 z-10 flex items-center gap-1 bg-background border rounded-lg shadow-lg p-1">
                                    <div
                                      {...provided.dragHandleProps}
                                      className="p-1 hover:bg-muted rounded cursor-move"
                                    >
                                      <GripVertical className="w-3 h-3" />
                                    </div>
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        moveElement(element.id, 'up');
                                      }}
                                    >
                                      <ChevronUp className="w-3 h-3" />
                                    </Button>
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        moveElement(element.id, 'down');
                                      }}
                                    >
                                      <ChevronDown className="w-3 h-3" />
                                    </Button>
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleVisibility(element.id, element.settings.isVisible ?? true);
                                      }}
                                    >
                                      {element.settings.isVisible ? (
                                        <Eye className="w-3 h-3" />
                                      ) : (
                                        <EyeOff className="w-3 h-3" />
                                      )}
                                    </Button>
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        duplicateElement(element);
                                      }}
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                    
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteElement(element.id);
                                      }}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}

                                {/* Element Name Badge */}
                                {selectedElement?.id === element.id && (
                                  <div className="absolute top-2 left-2 z-10">
                                    <div className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium">
                                      {element.name}
                                    </div>
                                  </div>
                                )}

                                {/* Actual Element Content */}
                                <ElementRenderer
                                  element={element}
                                  globalStyles={globalStyles}
                                  activeView={activeView}
                                  isSelected={selectedElement?.id === element.id}
                                  onUpdate={(updates) => onUpdateElement(element.id, updates)}
                                />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ) : (
            // Free Layout Mode
            <div className="relative w-full h-full min-h-[600px] overflow-hidden">
              {/* Grid overlay for better positioning */}
              <div 
                className="absolute inset-0 opacity-5 pointer-events-none"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, hsl(var(--primary)) 1px, transparent 1px),
                    linear-gradient(to bottom, hsl(var(--primary)) 1px, transparent 1px)
                  `,
                  backgroundSize: '20px 20px'
                }}
              />
              
              {elements.map((element) => {
                const isAbsolute = element.styles.position === 'absolute' || layoutMode === 'free';
                const elementStyle = isAbsolute ? {
                  position: 'absolute' as const,
                  left: element.styles.left || '50px',
                  top: element.styles.top || '50px',
                  zIndex: selectedElement?.id === element.id ? 20 : 
                           hoveredElement === element.id ? 15 : 
                           draggedElement === element.id ? 25 : 1
                } : {};
                
                return (
                  <div
                    key={element.id}
                    className={`group select-none ${
                      layoutMode === 'free' ? 'cursor-move' : ''
                    } ${
                      !element.settings.isVisible ? 'opacity-50' : ''
                    } ${
                      selectedElement?.id === element.id
                        ? 'ring-2 ring-primary ring-offset-2'
                        : hoveredElement === element.id
                        ? 'ring-1 ring-primary/50'
                        : ''
                    } ${
                      draggedElement === element.id ? 'opacity-75 scale-105 z-30' : ''
                    }`}
                    style={elementStyle}
                    onMouseEnter={() => setHoveredElement(element.id)}
                    onMouseLeave={() => setHoveredElement(null)}
                    onMouseDown={(e) => {
                      if (layoutMode === 'free') {
                        const target = e.target as HTMLElement;
                        
                        // Don't start dragging if clicking on interactive elements
                        if (target.closest('button') || target.closest('a') || target.closest('input')) {
                          e.stopPropagation();
                          return;
                        }
                        
                        // Select element first
                        onSelectElement(element);
                        
                        // Start drag operation
                        handleMouseDown(element.id, e);
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Only handle click if not dragging
                      if (!draggedElement) {
                        onSelectElement(element);
                      }
                    }}
                  >
                    {/* Free Mode Toolbar */}
                    {(selectedElement?.id === element.id || hoveredElement === element.id) && (
                      <div className="absolute -top-10 left-0 z-30 flex items-center gap-1 bg-background border rounded-lg shadow-lg p-1">
                        <div className="p-1 hover:bg-muted rounded cursor-move">
                          <Move className="w-3 h-3" />
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleVisibility(element.id, element.settings.isVisible ?? true);
                          }}
                        >
                          {element.settings.isVisible ? (
                            <Eye className="w-3 h-3" />
                          ) : (
                            <EyeOff className="w-3 h-3" />
                          )}
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicateElement(element);
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteElement(element.id);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}

                    {/* Element Name Badge with Position Info */}
                    {selectedElement?.id === element.id && (
                      <div className="absolute top-2 left-2 z-30">
                        <div className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium">
                          {element.name}
                          {layoutMode === 'free' && (
                            <span className="ml-2 opacity-75">
                              ({element.styles.left || '0'}, {element.styles.top || '0'})
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actual Element Content */}
                    <div className={draggedElement === element.id ? 'pointer-events-none' : ''}>
                      <ElementRenderer
                        element={element}
                        globalStyles={globalStyles}
                        activeView={activeView}
                        isSelected={selectedElement?.id === element.id}
                        onUpdate={(updates) => onUpdateElement(element.id, updates)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Canvas Footer */}
        <div className="w-full max-w-5xl mt-4 text-center">
          <p className="text-xs text-muted-foreground">
            Tipp: {layoutMode === 'linear' 
              ? 'Verwenden Sie die Toolbar zum Verschieben und Bearbeiten der Elemente.' 
              : 'Klicken und ziehen Sie Elemente, um sie frei zu positionieren.'
            }
          </p>
        </div>
      </div>
    </div>
  );
}